import styled from "styled-components";
import { Link } from "react-router-dom";
import { theme } from "styles/theme";

export const PageContainer = styled.div`
  height: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    flex-direction: column;
    align-items: stretch;
  }

  /* Mobile: same 0.75rem rhythm as the view switcher, 1rem before the content */
  @media (max-width: ${theme.breakpoints.mobile}) {
    gap: 0.75rem;
    margin-bottom: 1rem;
  }
`;

export const PageTitle = styled.h1`
  color: ${theme.colors.textPrimary};
  margin: 0;
`;

export const MapListContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: stretch;

  @media (max-width: ${theme.breakpoints.laptop}) {
    grid-template-columns: 1fr;
  }
`;

export const DevicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
`;

export const DeviceCard = styled.div<{
  $hasAlarm?: boolean;
  alarmSeverity?: string;
  /** Temporary dealer access: the left border is dashed instead of solid. */
  $temporary?: boolean;
}>`
  background-color: ${theme.colors.surface};
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition:
    transform 0.2s,
    box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  ${(props) =>
    props.$hasAlarm &&
    `border-left: 4px solid ${
      props.alarmSeverity === "Error"
        ? "#dc3545"
        : props.alarmSeverity === "Warning"
          ? "#ff9500"
          : "#28a745"
    };`}
  ${(props) =>
    // Active temporary access is always green; alarms stay visible via the icon.
    props.$temporary && `border-left: 4px dashed ${theme.colors.success};`}

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

export const DeviceName = styled.h3`
  margin-top: 0;
  margin-bottom: 0;
  color: ${theme.colors.textPrimary};
  display: inline-flex;
  align-items: center;
`;

export const DeviceInfo = styled.div<{ $column?: boolean }>`
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: ${(props) => (props.$column ? "column" : "row")};
  justify-content: ${(props) =>
    props.$column ? "flex-start" : "space-between"};
  gap: ${(props) => (props.$column ? "0.15rem" : "0")};
  align-items: ${(props) => (props.$column ? "flex-start" : "center")};
`;

export const InfoLabel = styled.span`
  color: ${theme.colors.darkGrey};
  font-size: 0.875rem;
`;

export const InfoValue = styled.span`
  font-weight: ${theme.fontWeights.medium};
  color: ${theme.colors.textPrimary};
  word-break: break-word;
`;

export const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  background-color: ${(props) => {
    switch (props.status) {
      case "accepted":
      case "temporary":
        return "#d4edda";
      case "pending":
        return "#fff3cd";
      case "rejected":
        return "#f8d7da";
      default:
        return "#e9ecef";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "accepted":
      case "temporary":
        return "#155724";
      case "pending":
        return "#856404";
      case "rejected":
        return "#721c24";
      default:
        return "#495057";
    }
  }};
`;

export const LinkButton = styled(Link)`
  padding: 0.5rem 0.75rem;
  background-color: ${theme.colors.primary};
  color: ${theme.colors.onPrimary};
  font-weight: ${theme.fontWeights.medium};
  text-align: center;
  text-decoration: none;
  font-size: 14px;
  height: 40px;
  font-family: "Noto Sans", sans-serif;
  border-radius: 4px;
  margin-top: auto;
  transition: background-color 0.2s;

  &:hover {
    background-color: #e6bd00;
    color: ${theme.colors.onPrimary};
  }
`;

export const DeleteLinkButton = styled.button`
  margin-top: auto;
  padding: 0.5rem 0.75rem;
  background-color: #dc3545;
  font-weight: ${theme.fontWeights.medium};
  font-size: 14px;
  height: 40px;
  color: white;
  font-family: "Noto Sans", sans-serif;
  text-align: center;
  transition: background-color 0.2s;
  border: none;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: #c82333;
  }
`;

export const DisabledDeviceCard = styled(DeviceCard)`
  opacity: 0.7;
  cursor: not-allowed;

  &:hover {
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

export const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.75rem;
  border-radius: 4px;
  font-size: 0.875rem;
  text-align: center;
  background-color: ${(props) => props.color || "#fff3cd"};
  color: ${(props) => (props.color === "#fff3cd" ? "#856404" : "#721c24")};
`;

export const WifiIcon = styled.i<{ connected: boolean | null }>`
  margin-right: 12px;
  font-size: 1.2rem;
  display: flex;
  justify-content: center;
  color: ${
    (props) =>
      props.connected === null
        ? "#999"
        : props.connected
          ? "#28a745" // verde para conectado
          : "#dc3545" // vermelho para desconectado
  };
`;

export const AlarmIndicator = styled.i<{
  $hasAlarm: boolean;
  alarmSeverity: string;
}>`
  font-size: 1.2rem;
  margin-left: 10px;
  color: ${(props) =>
    props.alarmSeverity === "Error"
      ? "#dc3545"
      : props.alarmSeverity === "Warning"
        ? "#ff9500"
        : "#28a745"};
`;

export const DeviceNameContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.75rem;
  gap: 8px;
`;

export const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
  background-color: ${theme.colors.surface};
  border-radius: 8px;
  padding: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

export const SearchBarContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 100%;
    justify-content: stretch;
  }

  /* Mobile: PageHeader gap handles the spacing (margin + gap made it 2rem) */
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: 0;
  }
`;

export const SearchInputContainer = styled.div`
  position: relative;
  width: 300px;

  @media (max-width: ${theme.breakpoints.tablet}) {
    width: 100%;
  }

  .p-input-icon-left {
    width: 100%;
  }

  input {
    width: 100%;
    padding-left: 35px;
    height: 40px; /* Default height to facilitate touch on mobile devices */

    @media (max-width: ${theme.breakpoints.mobile}) {
      height: 44px; /* Slightly taller height on mobile devices */
      font-size: 16px; /* Prevents automatic zoom on iOS */
    }
  }
`;

export const EditButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-left: 0.5rem;

  &:hover {
    background-color: ${theme.colors.primary}10;
    color: ${theme.colors.primaryDark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .pi {
    font-size: 0.875rem;
  }
`;


// Dashed (not solid) so a temporary-access request reads as temporary.
export const PendingTemporaryAccessCard = styled(DeviceCard)`
  border-left: 4px dashed ${theme.colors.warning};
`;

export const PendingTemporaryAccessIcon = styled.i`
  margin-right: 12px;
  color: ${theme.colors.warning};
  font-size: 1.2rem;
`;

export const PendingTemporaryAccessMessage = styled.p`
  margin: 0.75rem 0 0;
  color: ${theme.colors.textSecondary};
  font-size: ${theme.fontSizes.small};
  line-height: 1.45;
`;
